import Head from 'expo-router/head';
import * as AppConstants from '@/utilities/AppConstants';
import { BasicWrapperView } from '@/components/BasicWrapperView';
import { RabblsSearch } from '@/components/RabblsSearch';
import { CustomText } from '@/components/CustomText';
import { StyleSheet, View } from 'react-native';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Community, UNKNOWN_COMMUNITY } from '@/types/community';
import { Link, RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import { BasicScrollView } from '@/components/BasicScrollView';
import { Button, Hourglass, Panel } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import { AppContext } from '@/components/AppContext';
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConsts from '@/utilities/AppConsts';
import * as AppUtilities from '@/utilities/AppUtilities';
import { Collaboration, UNKNOWN_COLLABORATION } from '@/types/collaboration';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  header: {
    padding: 10,
    alignItems: 'center',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
  },
});

export default function ApproveCollab() {
  const router = useRouter();
  const { manage_community_id } = useLocalSearchParams<{ manage_community_id: string }>();
  const { manage_collab_id } = useLocalSearchParams<{ manage_collab_id: string }>();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [community, setCommunity] = useState(UNKNOWN_COMMUNITY);
  const [collaboration, setCollaboration] = useState<Collaboration>(UNKNOWN_COLLABORATION);
  const { showSpinner, hideSpinner } = useContext(AppContext);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      AppUtilities.info("ApproveCollab.loadInitialData started");
      const response = await AppUtilities.fetchAuthorized(
        "ApproveCollab.loadInitialData",
        "/rabbl/api/collaboration",
        "POST",
        {
          community_uniqueid: manage_community_id,
          collaboration_uniqueid: manage_collab_id,
        }
      );
      AppUtilities.info("ApproveCollab.loadInitialData finished:");
      AppUtilities.dir((response as any).body);
      const communityResult: Community = ((response as any).body as any).community;
      const collaborationResult: Collaboration = ((response as any).body as any).collaboration;
      setCommunity(communityResult);
      setCollaboration(collaborationResult);
    } catch (e) {
      AppUtilities.handleException(e, "ApproveCollab.loadInitialData");
      setError(AppUtilities.getErrors(e));
    } finally {
      setLoading(false);
    }
  }, [manage_community_id]);

  useEffect(() => {
    if (manage_community_id) {
      loadInitialData();
    }
  }, [manage_community_id, loadInitialData]);

  const onSubmit = useCallback(async () => {
    try {
      showSpinner();
      setError('');

      const response = await AppUtilities.fetchAuthorized(
        "ApproveCollab.onSubmit",
        "/rabbl/api/approvecollab",
        "POST",
        {
          collaboration_uniqueid: manage_collab_id,
        }
      );

      AppUtils.showInformationalToast("Collab approved");

      router.replace(("/manage/" + manage_community_id) as RelativePathString);

    } catch (e : any) {
      AppUtilities.handleException(e, "ApproveCollab.onSubmit");
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(), true);
    } finally {
      hideSpinner();
    }
  }, []);

  if (loading) {
    return (
      <View style={AppConstants.STYLES.container2}>
        <Hourglass size={AppConstants.LARGE_HOURGLASS_SIZE} />
      </View>
    );
  }

  if (error.length > 0 || !AppUtils.isValidCommunity(community)) {
    return (
      <View style={AppConstants.STYLES.container2}>
        <CustomText>Failed to load</CustomText>
        <AppText>{error}</AppText>
        <Button onPress={loadInitialData}>Retry</Button>
      </View>
    );
  }

  return (
    <BasicScrollView center={true}>
      <Meta title={"Approve collab"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>
          <CustomText>Approve Collab?</CustomText>
          <AppText>If you approve this collab with {community.name}, then users of both Rabbls will see each other until the collab expires on {AppUtils.parseAndDisplayISO8601DateString(collaboration.expires)}.</AppText>
          <Button onPress={onSubmit}>Approve</Button>
          <Link href="/rabbls" asChild={true} className={"nontext"}><Button>Cancel</Button></Link>
        </View>
      </Panel>
    </BasicScrollView>
  );
}
